import { Controller } from "@hotwired/stimulus"

// Job-seeker notification bell: polls the unread count and lazy-loads the
// right-to-left slide-over panel. ActionCable is dormant, so we poll (60s).
//
// The overlay + panel are teleported to <body> on connect: the app-shell top bar
// uses backdrop-filter, which establishes a containing block that would otherwise
// clip these position:fixed elements to the header instead of the viewport.
export default class extends Controller {
  static targets = ["badge", "overlay", "panel", "panelBody"]
  static values = {
    countUrl: String,
    panelUrl: String,
    interval: { type: Number, default: 60000 }
  }

  connect() {
    // Capture refs BEFORE moving the elements out of the controller's scope —
    // Stimulus target getters stop resolving once an element leaves this.element.
    this.overlayEl = this.overlayTarget
    this.panelEl = this.panelTarget
    this.panelBodyEl = this.panelBodyTarget
    document.body.appendChild(this.overlayEl)
    document.body.appendChild(this.panelEl)

    // The overlay is now outside the controller scope, so wire its close handler
    // manually instead of via data-action.
    this._onOverlayClick = () => this.close()
    this.overlayEl.addEventListener("click", this._onOverlayClick)

    this._onKey = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("keydown", this._onKey)

    // Turbo caches the page DOM on navigation. turbo:before-cache fires BEFORE
    // Turbo clones the page, so move the teleported nodes back inside the
    // controller element first — otherwise the cached snapshot keeps them at
    // <body> level, and on restore the Stimulus targets no longer resolve
    // ("Missing target"), breaking the bell. (disconnect alone is too late: it
    // runs after the snapshot is taken.)
    this._onBeforeCache = () => this.reclaimElements()
    document.addEventListener("turbo:before-cache", this._onBeforeCache)

    this.refreshCount()
    this.timer = setInterval(() => this.refreshCount(), this.intervalValue)
  }

  disconnect() {
    clearInterval(this.timer)
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    if (this.overlayEl) this.overlayEl.removeEventListener("click", this._onOverlayClick)
    this.reclaimElements()
  }

  // Return the teleported overlay + panel (closed) into the controller element,
  // so the element is whole for Turbo's cache snapshot and for teardown. Safe to
  // call repeatedly: it no-ops once the nodes are already back in place.
  reclaimElements() {
    this.close()
    if (this.overlayEl && this.overlayEl.parentElement !== this.element) {
      this.element.appendChild(this.overlayEl)
    }
    if (this.panelEl && this.panelEl.parentElement !== this.element) {
      this.element.appendChild(this.panelEl)
    }
  }

  toggle(event) {
    event.preventDefault()
    this.isOpen() ? this.close() : this.open()
  }

  isOpen() {
    return !this.panelEl.classList.contains("translate-x-full")
  }

  open() {
    this._previouslyFocused = document.activeElement
    this.panelEl.classList.remove("translate-x-full")
    this.overlayEl.classList.remove("hidden")
    this.panelEl.setAttribute("tabindex", "-1")
    this.panelEl.focus()
    this.loadPanel()
  }

  close() {
    this.panelEl.classList.add("translate-x-full")
    this.overlayEl.classList.add("hidden")
    if (this._previouslyFocused && this._previouslyFocused.focus) {
      this._previouslyFocused.focus()
    }
  }

  async loadPanel() {
    try {
      const res = await fetch(this.panelUrlValue, { headers: { "Accept": "text/html" }, credentials: "same-origin" })
      if (res.ok) {
        this.panelBodyEl.innerHTML = await res.text()
        this.refreshCount()
      }
    } catch (_e) {
      this.panelBodyEl.innerHTML = '<p class="px-4 py-6 text-sm text-on-surface-variant text-center">Could not load notifications</p>'
    }
  }

  async refreshCount() {
    try {
      const res = await fetch(this.countUrlValue, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      if (!res.ok) return
      const data = await res.json()
      this.renderBadge(data.count)
    } catch (_e) { /* keep last known badge */ }
  }

  renderBadge(count) {
    if (!count || count <= 0) {
      this.badgeTarget.hidden = true
      return
    }
    this.badgeTarget.hidden = false
    this.badgeTarget.textContent = count > 99 ? "99+" : String(count)
  }
}
